import { z } from 'zod';

export const legacyUserProvisioningAttributeFilter = z.object({
  attribute: z.enum(['job_title', 'department']),
  operator: z.enum(['is', 'is-not']),
  attribute_value: z.string(),
});

export const legacyUserProvisioningPresetFilter = z.object({
  /**
   * The ID corresponding to the card preset that should be applied if combination of the attribute filters
   * (using the combinator) match the SCIM User Resource.
   */
  card_preset_id: z.string(),
  /**
   * Controls how the attribute filters are combined
   */
  combinator: z.enum(['and', 'or']).optional(),
  /**
   * A list of filters that should be applied to a SCIM User Resource in order to check whether the
   * card preset specified by the card_preset_id should be applied.
   */
  attribute_filters: z.array(legacyUserProvisioningAttributeFilter),
});

/** The common fields for `demo` and `non-demo` accounts */
const baseOrgSchema = z.object({
  admin_user_id: z.string().optional(),
  show_business_onboarding: z.boolean().optional(),
  can_email_anyone: z.boolean().optional(),
  creation_timestamp_ms: z.number().optional(),
  name: z.string(),
  org_id: z.string(),
  avatar_url: z.string().optional().nullable(),
  company_size: z.string().optional().nullable(),
  valid_email_endings: z.array(z.string()).optional(),
  /**
   * When a user is provisioned from another service (e.g. Azure AD) we want to allow for their
   * card to be automatically assigned to a preset. However we can't assume that the org will only
   * have 1 preset (they might have 1 for each department) OR that they will want a preset assigned
   * to these cards. This field allows an admin to control which preset is applied to a provisioned
   * card if no filters match.
   */
  default_user_provisioning_preset_id: z.string().optional(),
  /**
   * An organization admin will have the ability to assign presets to provisioned cards depending on
   * a set of filters, e.g. Assign Preset A to this newly provisioned card if user is in Department A.
   * These filters take precedence over the default if there is a match.
   */
  user_provisioning_preset_filters: z.array(legacyUserProvisioningPresetFilter).optional(),
  /**
   * This controls whether an activation email is sent out automatically whenever a new card is provisioned
   * using an external user directory (e.g. Microsoft Azure AD).
   */
  auto_send_provisioned_user_activation_email: z.boolean().optional(),
  /**
   * This controls whether new contacts are synced automatically if there is any CRM that has been setup
   */
  auto_export_contacts: z.boolean().optional(),
  /**
   * May not exist for very old customers that are on the bundle plan
   */
  product_id: z.string().optional(),
  /** The ID of the company in HubSpot */
  hubspot_company_id: z.string().optional(),
  /** Derived data */
  totals: z
    .object({
      /** How many non-deleted cards created for an org */
      cards_created: z.number().optional(),
      /** How many non-deleted cards that are activated */
      cards_activated: z.number().optional(),
    })
    .optional(),
});

const baseNonDemoOrgSchema = z.object({
  max_cards: z.number().optional(),
  override_max_cards: z.number().optional(),
  /**
   * May not exist for very old customers that are on the bundle plan
   * should probably be of type  z.custom<FirebaseFirestore.DocumentReference<StripeSubscription>>() ;
   */
  subscription: z.any().optional(),
  subscription_id: z.string().optional(),
  /**
   * Keep track of the cards that have been paid for in the current billing cycle.
   * This is handy for telling when to turn on and off proration for a subscription in Stripe.
   */
  paid_for_card_count: z.number().optional(),
  /**
   * May not exist for very old customers that are on the bundle plan
   */
  product_id: z.string().optional(),
});

/** The fields for `non-demo` accounts */
export const legacyNonDemoOrgSchema = z
  .object({
    is_demo_account: z.literal(false),
  })
  .merge(baseOrgSchema)
  .merge(baseNonDemoOrgSchema);

/** The fields for `non-demo` accounts that don't have is_demo_account set  */
export const legacyInvalidNonDemoOrgSchema = z
  .object({
    /**
     * `.optional()` is required for this branch to match documents where the key is absent:
     * since zod 4.4, a missing key no longer satisfies a bare `z.literal(undefined)`.
     */
    is_demo_account: z.literal(undefined).optional(),
  })
  .merge(baseOrgSchema)
  .merge(baseNonDemoOrgSchema);

/** The fields for `demo` accounts */
export const legacyDemoOrgSchema = z
  .object({
    is_demo_account: z.literal(true),
    /** most likely a boolean, but we are not sure. */
    demo_expired: z.unknown().optional(),
  })
  .merge(baseOrgSchema);

export const legacyBBOrgSchema = z.discriminatedUnion('is_demo_account', [
  legacyDemoOrgSchema,
  legacyNonDemoOrgSchema,
  legacyInvalidNonDemoOrgSchema,
]);

export type LegacyBaseBBOrg = z.infer<typeof baseOrgSchema>;
export type LegacyBBOrg = z.infer<typeof legacyBBOrgSchema>;

export function isDBBBOrg(candidate: unknown): candidate is LegacyBBOrg {
  return legacyBBOrgSchema.safeParse(candidate).success;
}
